import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { addLead, uploadPhotos } from '../hooks/useFirestore';

const WA_DEFAULT = 'https://wa.me/971585338786?text=Hi%20I%20want%20to%20sell%20my%20e-bike';
const BRANDS = ['Surron', 'Talaria', 'Kugoo', 'Zero', 'Segway', 'Other'];
const CONDITIONS = ['Brand New', 'Like New', 'Good', 'Fair'];
const BATTERY_CONDITIONS = ['Excellent', 'Good', 'Fair', 'Needs Replacement'];

export default function Sell() {
  const [form, setForm] = useState({
    name: '', phone: '', brand: '', model: '', year: '', askingPrice: '',
    condition: '', battery: '', location: '', reason: ''
  });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files).slice(0, 5);
    setFiles(selected);
    setPreviews(selected.map(f => URL.createObjectURL(f)));
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.brand || !form.model) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      const leadId = await addLead(form);

      if (files.length > 0) {
        toast('Uploading photos...', { icon: '📷' });
        const photoUrls = await uploadPhotos('submissions', leadId, files);
        // Update lead with photo URLs
        const { updateLead } = await import('../hooks/useFirestore');
        await updateLead(leadId, { photos: photoUrls });
      }

      setSubmitted(true);
      toast.success('Submission received!');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try WhatsApp instead.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="fade-in" style={{ maxWidth: 600, margin: '0 auto', padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontSize: 24, marginBottom: 12 }}>Thank You!</h2>
        <p style={{ color: 'var(--text-2)', marginBottom: 24, lineHeight: 1.6 }}>
          The eBike Dubai team will review your bike and contact you within 24 hours via WhatsApp.
        </p>
        <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <button className="btn-wa" style={{ fontSize: 16, padding: '14px 32px' }}>💬 Chat with us now</button>
        </a>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ maxWidth: 600, margin: '0 auto', padding: '32px 20px' }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Sell Your E-Bike</h1>
      <p style={{ color: 'var(--text-2)', marginBottom: 32 }}>Fill in the details below. We'll review and get back to you within 24 hours.</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Your Name *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>WhatsApp / Mobile *</label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+971..." />
        </div>
        <div className="grid-2">
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Brand *</label>
            <select value={form.brand} onChange={e => set('brand', e.target.value)}>
              <option value="">Select brand</option>
              {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Model *</label>
            <input value={form.model} onChange={e => set('model', e.target.value)} placeholder="e.g. Light Bee X" />
          </div>
        </div>
        <div className="grid-2">
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Year</label>
            <input value={form.year} onChange={e => set('year', e.target.value)} placeholder="2024" />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Asking Price (AED)</label>
            <input value={form.askingPrice} onChange={e => set('askingPrice', e.target.value)} placeholder="15,000" />
          </div>
        </div>
        <div className="grid-2">
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Condition</label>
            <select value={form.condition} onChange={e => set('condition', e.target.value)}>
              <option value="">Select</option>
              {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Battery Health</label>
            <select value={form.battery} onChange={e => set('battery', e.target.value)}>
              <option value="">Select</option>
              {BATTERY_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Location</label>
          <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Dubai Marina" />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Reason for Selling</label>
          <textarea value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Upgrading, relocating, etc." />
        </div>

        {/* Photo Upload */}
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Photos (up to 5)</label>
          <div className="photo-upload-zone" onClick={() => fileRef.current?.click()}>
            <p style={{ color: 'var(--text-2)', fontSize: 14 }}>📷 Click to upload photos of your bike</p>
            <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 4 }}>JPG, PNG — max 5 photos, 5MB each</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: 'none' }} />
          {previews.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {previews.map((src, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={src} alt={`Preview ${i + 1}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--dark-4)' }} />
                  <button onClick={() => removeFile(i)} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--danger)', color: 'white', border: 'none', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          className="btn-volt"
          style={{ width: '100%', justifyContent: 'center', padding: 16, fontSize: 16, marginTop: 8 }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (files.length > 0 ? 'Uploading photos...' : 'Submitting...') : 'Submit for Review'}
        </button>

        <p style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>Or WhatsApp us directly for a faster response</p>
        <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <button className="btn-wa" style={{ width: '100%', justifyContent: 'center' }}>💬 Send via WhatsApp instead</button>
        </a>
      </div>
    </div>
  );
}
